import type { TrBlock, TrField, TrValues, TrVertragsart } from "./types";

/**
 * Lesen, Prüfen und Anzeigen der Werte der Transport-Vertragsmaske.
 *
 * Ein Feld speichert je nach Typ etwas anderes: eine Zeichenkette
 * (`single`, `eur`, `text`), eine Liste (`multi`), ein Objekt aus Teilwerten
 * (`combo`, `eurSelect`) oder eine Zeilenliste (`subtable`). Die Zugriffe
 * liegen deshalb an einer Stelle statt in jeder Komponente erneut.
 */

export const getText = (values: TrValues, key: string): string => {
  const v = values[key];
  return typeof v === "string" ? v : "";
};

export const getList = (values: TrValues, key: string): string[] => {
  const v = values[key];
  return Array.isArray(v) && v.every((e) => typeof e === "string") ? (v as string[]) : [];
};

export const getRecord = (values: TrValues, key: string): Record<string, string> => {
  const v = values[key];
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, string>) : {};
};

export const getRows = (values: TrValues, key: string): Record<string, string>[] => {
  const v = values[key];
  if (!Array.isArray(v)) return [];
  return v.filter((e): e is Record<string, string> => typeof e === "object" && e !== null);
};

/** Trägt das Feld einen Wert? Grundlage für Zähler und Übersicht. */
export const isFilled = (field: TrField, values: TrValues): boolean => {
  switch (field.type) {
    case "multi":
      return getList(values, field.key).length > 0;
    case "combo":
    case "eurSelect": {
      const rec = getRecord(values, field.key);
      return Object.values(rec).some((v) => String(v ?? "").trim() !== "");
    }
    case "subtable":
      return getRows(values, field.key).some((row) =>
        Object.entries(row).some(([k, v]) => k !== "zeile" && String(v ?? "").trim() !== ""),
      );
    case "readout":
      // Anzeigefelder stammen aus der Analyse — sie zählen nicht als Eingabe.
      return false;
    default:
      return getText(values, field.key).trim() !== "";
  }
};

/** Felder eines Blocks, die aktuell sichtbar sind (`showWhen`). */
export const visibleFields = (block: TrBlock, values: TrValues): TrField[] =>
  block.fields.filter((f) => !f.showWhen || f.showWhen(values));

/** „3 von 8" — wie viele Eingabefelder des Blocks gefüllt sind. */
export const blockCounts = (block: TrBlock, values: TrValues) => {
  const fields = visibleFields(block, values).filter((f) => f.type !== "readout");
  return { filled: fields.filter((f) => isFilled(f, values)).length, total: fields.length };
};

/** Gefüllte und mögliche Eingabefelder einer ganzen Vertragsart. */
export const vertragsartCounts = (art: TrVertragsart, values: TrValues) =>
  art.blocks.reduce(
    (acc, b) => {
      const c = blockCounts(b, values);
      return { filled: acc.filled + c.filled, total: acc.total + c.total };
    },
    { filled: 0, total: 0 },
  );

/**
 * Eine Vertragsart gilt als bearbeitet, sobald irgendein Feld einen Wert
 * trägt — davon hängt der Statuspunkt auf der Übersichtskarte ab.
 */
export const hatDaten = (art: TrVertragsart, values: TrValues): boolean =>
  vertragsartCounts(art, values).filled > 0;

const EUR = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2,
});

/** Betragseingabe („1234,50", „1.234,50") als Zahl. */
export const parseEur = (raw: string): number | null => {
  const s = raw.trim().replace(/\./g, "").replace(",", ".");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export const formatEur = (raw: string): string => {
  const n = parseEur(raw);
  return n === null ? raw : EUR.format(n);
};

/**
 * Feldwert als eine Zeile Text — für die Übersichtskarten und überall dort,
 * wo kein Eingabefeld steht. Leere Felder ergeben „—".
 */
export const formatValue = (field: TrField, values: TrValues): string => {
  switch (field.type) {
    case "eur": {
      const raw = getText(values, field.key);
      return raw.trim() === "" ? "—" : formatEur(raw);
    }
    case "multi": {
      const list = getList(values, field.key);
      return list.length ? list.join(", ") : "—";
    }
    case "combo":
    case "eurSelect": {
      const rec = getRecord(values, field.key);
      const teile = (field.parts ?? [])
        .map((p) => {
          const v = String(rec[p.key] ?? "").trim();
          if (v === "") return null;
          if (p.type === "eur") return formatEur(v);
          if (p.type === "percent") return `${v} %`;
          return v;
        })
        .filter(Boolean);
      return teile.length ? teile.join(" · ") : "—";
    }
    case "subtable": {
      const rows = getRows(values, field.key).filter((r) =>
        Object.entries(r).some(([k, v]) => k !== "zeile" && String(v ?? "").trim() !== ""),
      );
      if (rows.length === 0) return "—";
      // Bei einer einzigen belegten Zeile den Wert selbst zeigen, sonst zählen.
      if (rows.length === 1) {
        const row = rows[0];
        const erste = field.subtable?.columns.find(
          (c) => String(row[c.key] ?? "").trim() !== "",
        );
        const wert = erste ? String(row[erste.key]) : "";
        const label = String(row.zeile ?? "");
        const gezeigt = erste?.type === "eur" ? formatEur(wert) : wert;
        return label ? `${label}: ${gezeigt}` : gezeigt;
      }
      return `${rows.length} Einträge`;
    }
    default: {
      const raw = getText(values, field.key);
      return raw.trim() === "" ? "—" : raw;
    }
  }
};
